import { useEffect } from "react";
import SEOHead from "@/components/SEOHead";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Target, Zap, BarChart3, Users, CheckCircle, ArrowRight, MapPin } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import accompagnementQuebec from "@/assets/accompagnement-quebec.jpg";

declare global {
  interface Window {
    Cal?: any;
  }
}

const initCalEmbed = () => {
  if (typeof window !== "undefined" && !window.Cal) {
    (function (C: any, A: string, L: string) {
      let p = function (a: any, ar: any) {
        a.q.push(ar);
      };
      let d = C.document;
      C.Cal =
        C.Cal ||
        function () {
          let cal = C.Cal;
          let ar = arguments;
          if (!cal.loaded) {
            cal.ns = {};
            cal.q = cal.q || [];
            d.head.appendChild(d.createElement("script")).src = A;
            cal.loaded = true;
          }
          if (ar[0] === L) {
            const api = function () {
              p(api, arguments);
            };
            const namespace = ar[1];
            api.q = api.q || [];
            if (typeof namespace === "string") {
              cal.ns[namespace] = cal.ns[namespace] || api;
              p(cal.ns[namespace], ar);
              p(cal, ["initNamespace", namespace]);
            } else p(cal, ar);
            return;
          }
          p(cal, ar);
        };
    })(window, "https://app.cal.com/embed/embed.js", "init");

    window.Cal("init", "premier-contact-accompagnement-strategique", { origin: "https://app.cal.com" });
    window.Cal.ns["premier-contact-accompagnement-strategique"]("ui", { hideEventTypeDetails: false, layout: "month_view" });
  }
};

const AccompagnementQuebec = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    initCalEmbed();
  }, []);

  const openCalendar = () => {
    if (window.Cal) {
      window.Cal.ns["premier-contact-accompagnement-strategique"]("openModal", {
        calLink: "maxim-rousseau-6ciolz/premier-contact-accompagnement-strategique",
      });
    }
  };

  const benefits = [
    { icon: Target, title: "Stratégie claire", desc: "Un plan marketing adapté à votre marché québécois" },
    { icon: Zap, title: "Génération de leads", desc: "Un système structuré pour attirer des prospects qualifiés" },
    { icon: BarChart3, title: "Message cohérent", desc: "Une communication alignée sur tous les canaux" },
    { icon: Users, title: "Partenaire impliqué", desc: "Un accompagnement axé sur votre croissance" },
  ];

  const forWho = [
    "Entrepreneurs québécois",
    "PME en croissance",
    "Marques personnelles",
    "Entreprises B2B",
    "Organisations qui veulent des leads récurrents",
  ];

  const notForWho = [
    "Ceux qui veulent \"juste des publications\"",
    "Ceux qui cherchent une solution miracle",
    "Ceux qui veulent le prix le plus bas",
  ];

  const steps = [
    { num: "1", title: "Appel stratégique", desc: "On discute de votre situation et vos objectifs" },
    { num: "2", title: "Analyse", desc: "On évalue votre situation marketing actuelle" },
    { num: "3", title: "Stratégie", desc: "On définit le plan d'action" },
    { num: "4", title: "Mise en place", desc: "Contenu et publicité déployés" },
    { num: "5", title: "Optimisation", desc: "Amélioration continue des résultats" },
  ];

  return (
    <>
      <SEOHead
        title="Accompagnement marketing au Québec pour générer des leads | Agence 80/20"
        description="Agence 80/20 accompagne les entrepreneurs et PME au Québec pour générer des leads qualifiés grâce au contenu stratégique et à la publicité payante."
        canonical="/accompagnement-marketing-quebec"
      />

      <Navbar />

      <main className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="relative py-20 md:py-32 overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img 
              src={accompagnementQuebec} 
              alt="Accompagnement marketing au Québec" 
              className="w-full h-full object-cover opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background via-background/95 to-background" />
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-4xl mx-auto text-center"
            >
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-6">
                <MapPin className="h-4 w-4" />
                <span className="text-sm font-medium">Exclusivement au Québec</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-tight">
                Accompagnement marketing au Québec pour <span className="text-primary">générer des leads</span>
              </h1>
              
              <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
                Agence 80/20 accompagne exclusivement des entrepreneurs et PME au Québec qui veulent générer des leads qualifiés grâce à une approche marketing structurée, axée sur le contenu stratégique et la publicité payante.
              </p>
              
              <p className="text-lg text-foreground font-medium mb-8">
                Ici, pas de marketing improvisé. Chaque action est pensée pour transformer la visibilité en opportunités d'affaires réelles.
              </p>
              
              <Button 
                size="lg" 
                onClick={openCalendar}
                className="bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                Réserver un appel stratégique
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </motion.div>
          </div>
        </section>

        {/* Quebec Context Section */}
        <section className="py-16 md:py-24 bg-muted/30">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-4xl mx-auto"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6 text-center">
                Un accompagnement marketing adapté à la réalité du Québec
              </h2>
              
              <p className="text-lg text-muted-foreground mb-8 text-center">
                Le marché québécois a ses particularités :
              </p>
              
              <div className="grid md:grid-cols-2 gap-4 mb-8">
                {["PME en croissance", "Entrepreneurs très sollicités", "Forte concurrence locale", "Décisions basées sur la confiance"].map((item, index) => (
                  <div key={index} className="flex items-center gap-3 bg-background p-4 rounded-lg border border-border">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                    <span className="text-foreground">{item}</span>
                  </div>
                ))}
              </div>
              
              <p className="text-lg text-muted-foreground text-center">
                Un accompagnement marketing efficace au Québec doit tenir compte de ces réalités. C'est exactement ce que fait Agence 80/20 : adapter la stratégie au contexte local, sans copier des modèles génériques.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Problem Section */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-4xl mx-auto"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6 text-center">
                Pourquoi le marketing ne génère pas assez de leads pour les entreprises au Québec
              </h2>
              
              <p className="text-lg text-muted-foreground mb-8 text-center">
                Beaucoup d'entreprises au Québec rencontrent les mêmes problèmes :
              </p>
              
              <div className="grid md:grid-cols-2 gap-4 mb-8">
                {[
                  "Du contenu publié sans stratégie claire",
                  "Des publicités payantes qui coûtent cher",
                  "Peu de synergie entre l'organique et le payant",
                  "Aucun système pour qualifier les leads"
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-3 bg-destructive/5 p-4 rounded-lg border border-destructive/20">
                    <span className="text-destructive font-bold">✕</span>
                    <span className="text-foreground">{item}</span>
                  </div>
                ))}
              </div>
              
              <p className="text-lg text-foreground font-medium text-center">
                Résultat : des efforts marketing constants, mais peu de résultats concrets.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Approach Section */}
        <section className="py-16 md:py-24 bg-muted/30">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-5xl mx-auto"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 text-center">
                L'approche Agence 80/20 : contenu + publicité = résultats
              </h2>
              
              <p className="text-lg text-muted-foreground mb-12 text-center">
                Notre accompagnement marketing au Québec repose sur une approche simple et efficace
              </p>
              
              <div className="grid md:grid-cols-3 gap-8">
                <div className="bg-background p-6 rounded-xl border border-border">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                    <span className="text-2xl font-bold text-primary">1</span>
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-3">Clarifier l'offre et le positionnement</h3>
                  <p className="text-muted-foreground mb-4">Avant de créer du contenu ou des publicités, on définit :</p>
                  <ul className="space-y-2">
                    {["Votre client idéal au Québec", "Votre message principal", "Votre avantage concurrentiel"].map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-foreground">
                        <CheckCircle className="h-4 w-4 text-primary" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="bg-background p-6 rounded-xl border border-border">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                    <span className="text-2xl font-bold text-primary">2</span>
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-3">Créer du contenu stratégique</h3>
                  <p className="text-muted-foreground mb-4">Du contenu pensé pour :</p>
                  <ul className="space-y-2">
                    {["Éduquer votre audience", "Créer de la confiance", "Positionner votre expertise"].map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-foreground">
                        <CheckCircle className="h-4 w-4 text-primary" />
                        {item}
                      </li>
                    ))}
                  </ul>
                  <p className="text-muted-foreground mt-4 text-sm">Chaque contenu a un rôle précis dans le parcours du prospect.</p>
                </div>
                
                <div className="bg-background p-6 rounded-xl border border-border">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                    <span className="text-2xl font-bold text-primary">3</span>
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-3">Amplifier avec la publicité payante</h3>
                  <p className="text-muted-foreground mb-4">La publicité payante permet de :</p>
                  <ul className="space-y-2">
                    {["Accélérer la génération de leads", "Toucher les bonnes personnes", "Tester et optimiser les messages"].map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-foreground">
                        <CheckCircle className="h-4 w-4 text-primary" />
                        {item}
                      </li>
                    ))}
                  </ul>
                  <p className="text-muted-foreground mt-4 text-sm">Le contenu nourrit la publicité, et la publicité amplifie le contenu.</p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* For Who Section */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-5xl mx-auto"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-12 text-center">
                À qui s'adresse cet accompagnement marketing au Québec
              </h2>
              
              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-primary/5 p-6 rounded-xl border border-primary/20">
                  <h3 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                    <CheckCircle className="h-6 w-6 text-primary" />
                    C'est idéal pour :
                  </h3>
                  <ul className="space-y-3">
                    {forWho.map((item, i) => (
                      <li key={i} className="flex items-center gap-3 text-foreground">
                        <CheckCircle className="h-4 w-4 text-primary flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div className="bg-muted/50 p-6 rounded-xl border border-border">
                  <h3 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                    <span className="text-muted-foreground">✕</span>
                    Ce n'est pas pour :
                  </h3>
                  <ul className="space-y-3">
                    {notForWho.map((item, i) => (
                      <li key={i} className="flex items-center gap-3 text-muted-foreground">
                        <span className="text-muted-foreground">✕</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-16 md:py-24 bg-muted/30">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-5xl mx-auto"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 text-center">
                Les bénéfices concrets d'un accompagnement marketing local
              </h2>
              
              <p className="text-lg text-muted-foreground mb-12 text-center">
                En travaillant avec Agence 80/20, vous obtenez :
              </p>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {benefits.map((benefit, index) => (
                  <div key={index} className="bg-background p-6 rounded-xl border border-border text-center">
                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-4">
                      <benefit.icon className="h-6 w-6 text-primary" />
                    </div>
                    <h3 className="font-bold text-foreground mb-2">{benefit.title}</h3>
                    <p className="text-sm text-muted-foreground">{benefit.desc}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Why Quebec Agency Section */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-4xl mx-auto"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6 text-center">
                Pourquoi choisir une agence marketing québécoise
              </h2>
              
              <p className="text-lg text-muted-foreground mb-8 text-center">
                Travailler avec une agence marketing au Québec, c'est :
              </p>
              
              <div className="grid md:grid-cols-2 gap-4 mb-8">
                {[
                  "Comprendre la culture et le marché local",
                  "Parler le même langage que vos clients",
                  "Adapter les messages aux réalités d'ici",
                  "Être plus pertinent dans la communication"
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3 bg-primary/5 p-4 rounded-lg border border-primary/20">
                    <MapPin className="h-5 w-5 text-primary flex-shrink-0" />
                    <span className="text-foreground">{item}</span>
                  </div>
                ))}
              </div>
              
              <p className="text-lg text-foreground font-medium text-center">
                Agence 80/20 travaille exclusivement avec des entreprises du Québec, ce qui permet une approche plus précise et plus efficace.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-16 md:py-24 bg-muted/30">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-5xl mx-auto"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-12 text-center">
                Comment débute l'accompagnement marketing
              </h2>
              
              <div className="grid md:grid-cols-5 gap-4">
                {steps.map((step, index) => (
                  <div key={index} className="text-center">
                    <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center mx-auto mb-3 font-bold text-lg">
                      {step.num}
                    </div>
                    <h3 className="font-bold text-foreground mb-1">{step.title}</h3>
                    <p className="text-sm text-muted-foreground">{step.desc}</p>
                  </div>
                ))}
              </div>
              
              <p className="text-lg text-muted-foreground text-center mt-8">
                Chaque étape est pensée pour générer des résultats mesurables.
              </p>
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-3xl mx-auto text-center"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
                Prêt à générer plus de leads au Québec ?
              </h2>
              
              <p className="text-lg text-muted-foreground mb-8">
                Si vous êtes un entrepreneur ou une PME au Québec et que vous voulez structurer votre marketing pour obtenir des leads qualifiés de façon constante, l'accompagnement marketing d'Agence 80/20 est conçu pour ça.
              </p>
              
              <Button 
                size="lg" 
                onClick={openCalendar}
                className="bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                Réserver un appel stratégique
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </motion.div>
          </div>
        </section>

        {/* Related Articles Section */}
        <section className="py-16 md:py-24 bg-muted/30">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-4xl mx-auto"
            >
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-8 text-center">
                Pages et articles reliés
              </h2>
              
              <div className="grid md:grid-cols-2 gap-4">
                <Link 
                  to="/accompagnement-marketing-montreal"
                  className="bg-primary/5 p-4 rounded-lg border border-primary/20 hover:border-primary/50 transition-colors"
                >
                  <h3 className="font-bold text-foreground mb-2">Accompagnement marketing à Montréal</h3>
                  <p className="text-sm text-muted-foreground">Notre accompagnement pour les entrepreneurs et PME à Montréal.</p>
                </Link>
                
                <Link 
                  to="/accompagnement-marketing-laval"
                  className="bg-primary/5 p-4 rounded-lg border border-primary/20 hover:border-primary/50 transition-colors"
                >
                  <h3 className="font-bold text-foreground mb-2">Accompagnement marketing à Laval</h3>
                  <p className="text-sm text-muted-foreground">Notre accompagnement pour les entrepreneurs et PME à Laval.</p>
                </Link>
                
                <Link 
                  to="/accompagnement-marketing-rive-sud"
                  className="bg-primary/5 p-4 rounded-lg border border-primary/20 hover:border-primary/50 transition-colors"
                >
                  <h3 className="font-bold text-foreground mb-2">Accompagnement marketing sur la Rive-Sud</h3>
                  <p className="text-sm text-muted-foreground">Notre accompagnement pour les entrepreneurs et PME sur la Rive-Sud.</p>
                </Link>
                
                <Link 
                  to="/articles/accompagnement-marketing-generer-des-leads"
                  className="bg-background p-4 rounded-lg border border-border hover:border-primary/50 transition-colors"
                >
                  <h3 className="font-bold text-foreground mb-2">Accompagnement marketing : générer des leads grâce au contenu</h3>
                  <p className="text-sm text-muted-foreground">Découvrez comment un accompagnement marketing structuré permet de générer des leads.</p>
                </Link>
                
                <Link 
                  to="/articles/generation-de-leads-entrepreneurs"
                  className="bg-background p-4 rounded-lg border border-border hover:border-primary/50 transition-colors"
                >
                  <h3 className="font-bold text-foreground mb-2">Génération de leads pour entrepreneurs</h3>
                  <p className="text-sm text-muted-foreground">La méthode qui fonctionne vraiment pour générer des leads qualifiés.</p>
                </Link>
                
                <Link 
                  to="/articles/contenu-organique-vs-publicite-payante"
                  className="bg-background p-4 rounded-lg border border-border hover:border-primary/50 transition-colors"
                >
                  <h3 className="font-bold text-foreground mb-2">Contenu organique vs publicité payante</h3>
                  <p className="text-sm text-muted-foreground">Quoi choisir pour générer des leads efficacement.</p>
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default AccompagnementQuebec;
